import { useRef, useEffect } from 'react';

/**
 * OceanSurface — 海面动态
 * 
 * 使用 Canvas 渐变 + 极慢位移营造海面光影呼吸感。
 * 周期 15~30 秒，用户几乎察觉不到动画存在，
 * 但能感受到"海面是活的"。
 */

export default function OceanSurface() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animId;
    let startTime = performance.now();

    const resize = () => {
      canvas.width = canvas.offsetWidth * 2;
      canvas.height = 200;
    };
    resize();
    window.addEventListener('resize', resize);

    const draw = (now) => {
      const elapsed = (now - startTime) / 1000;
      const w = canvas.width;
      const h = canvas.height;

      ctx.clearRect(0, 0, w, h);

      // 海面光影层 — 极慢位移渐变
      const gradient = ctx.createLinearGradient(0, 0, 0, h);

      // 低频光影变化
      const t1 = Math.sin(elapsed * 0.15) * 0.5 + 0.5;
      const t2 = Math.sin(elapsed * 0.1 + 1.2) * 0.5 + 0.5;
      const t3 = Math.sin(elapsed * 0.08 + 2.5) * 0.5 + 0.5;

      gradient.addColorStop(0, 'rgba(8, 29, 50, 0)');
      gradient.addColorStop(0.2, `rgba(10, 40, 74, ${0.04 + t1 * 0.03})`);
      gradient.addColorStop(0.4, `rgba(15, 55, 95, ${0.06 + t2 * 0.04})`);
      gradient.addColorStop(0.6, `rgba(20, 70, 115, ${0.05 + t3 * 0.03})`);
      gradient.addColorStop(0.8, `rgba(12, 45, 80, ${0.04 + t1 * 0.02})`);
      gradient.addColorStop(1, 'rgba(6, 24, 42, 0)');

      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, w, h);

      // 微光反射 — 极淡的横向光带
      const lightX = w * 0.3 + Math.sin(elapsed * 0.12) * w * 0.25;
      const lightW = w * 0.15;
      const lightGrad = ctx.createRadialGradient(lightX, h * 0.4, 0, lightX, h * 0.4, lightW);
      lightGrad.addColorStop(0, `rgba(88, 183, 232, ${0.025 + t2 * 0.015})`);
      lightGrad.addColorStop(1, 'rgba(88, 183, 232, 0)');
      ctx.fillStyle = lightGrad;
      ctx.fillRect(0, 0, w, h);

      // 第二条微弱光反射
      const light2X = w * 0.7 + Math.sin(elapsed * 0.09 + 3) * w * 0.15;
      const light2Grad = ctx.createRadialGradient(light2X, h * 0.6, 0, light2X, h * 0.6, lightW * 0.7);
      light2Grad.addColorStop(0, `rgba(242, 185, 75, ${0.015 + t3 * 0.01})`);
      light2Grad.addColorStop(1, 'rgba(242, 185, 75, 0)');
      ctx.fillStyle = light2Grad;
      ctx.fillRect(0, 0, w, h);

      animId = requestAnimationFrame(draw);
    };

    animId = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        bottom: '8%',
        left: 0,
        right: 0,
        width: '100%',
        height: '200px',
        pointerEvents: 'none',
        zIndex: 2,
        opacity: 0.9,
      }}
    />
  );
}
